import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import {
  PageHeader, Card, Badge, Btn, Modal, FormGroup, DataTable, Td, Tr,
  Avatar, SearchBar, FilterBar, FilterSelect, Paginator, ResultCount, ActiveFilters,
} from '../components/UI';

const CYLINDERS = ['14.2 kg (Regular)','5 kg (Small)','19 kg (Commercial)','47.5 kg (Commercial Large)'];
const EMPTY = { name:'', phone:'', email:'', address:'', connectionNo:'', aadhar:'', cylinderType:'14.2 kg (Regular)', status:'Active' };
const STATUS_CONF = {
  Delivered:         { type:'success' },
  Pending:           { type:'warning' },
  'Out for Delivery':{ type:'info' },
  Cancelled:         { type:'danger' },
};
const STATUS_OPTS = [
  { value:'All', label:'All Status' },
  { value:'Active', label:'Active' },
  { value:'Inactive', label:'Inactive' },
];
const CYL_OPTS = [
  { value:'All', label:'All Cylinders' },
  ...CYLINDERS.map(c => ({ value:c, label:c.split(' ').slice(0,2).join(' ') })),
];

export default function Customers() {
  const { user } = useAuth();
  const canAdmin = ['admin','super'].includes(user?.role);

  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterStatus, setFilterStatus]   = useState('All');
  const [filterCylinder, setFilterCylinder] = useState('All');
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(20);
  const [modal, setModal]         = useState(null);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [hist, setHist]           = useState(null);
  const [histBk, setHistBk]       = useState([]);
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getCustomers({ search, status:filterStatus, limit:500 });
      let list = data.customers || [];
      if (filterCylinder !== 'All') list = list.filter(c => c.cylinderType === filterCylinder);
      setRows(list);
      setTotal(data.total || list.length);
      setPage(1);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterCylinder]);

  useEffect(() => { load(); }, [load]);

  const paged      = rows.slice((page-1)*perPage, page*perPage);
  const totalPages = Math.ceil(rows.length / perPage);

  const openAdd  = () => { setEditId(null); setForm(EMPTY); setModal('form'); };
  const openEdit = c => {
    setEditId(c.id);
    setForm({ name:c.name, phone:c.phone, email:c.email||'', address:c.address||'', connectionNo:c.connectionNo, aadhar:c.aadhar||'', cylinderType:c.cylinderType, status:c.status });
    setModal('form');
  };
  const openHist = async c => {
    setHist(c); setModal('history'); setHistBk([]);
    const { data } = await api.getCustomer(c.id);
    setHistBk(data.bookings || []);
  };

  const save = async () => {
    if (!form.name || !form.phone || !form.connectionNo) return toast.error('Name, phone and connection no. required');
    setSaving(true);
    try {
      if (editId) { await api.updateCustomer(editId, form); toast.success('Customer updated'); }
      else { await api.createCustomer(form); toast.success('Customer added'); }
      setModal(null); load();
    } catch(err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async id => {
    if (!window.confirm('Delete this customer?')) return;
    try { await api.deleteCustomer(id); toast.success('Customer deleted'); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const F = (k, v) => setForm(f => ({ ...f, [k]:v }));

  const activeFilters = [
    ...(filterStatus !== 'All' ? [{ label:`Status: ${filterStatus}`, key:'status' }] : []),
    ...(filterCylinder !== 'All' ? [{ label:`Cylinder: ${filterCylinder.split(' ')[0]}`, key:'cylinder' }] : []),
  ];

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle={`${total} total customers in the system`}
        action={<Btn onClick={openAdd} icon="＋">Add Customer</Btn>}
      />

      <FilterBar actions={<ResultCount count={rows.length} total={total} label="customer" />}>
        <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search name, phone, connection..." />
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} options={STATUS_OPTS} style={{ width:140 }} />
        <FilterSelect value={filterCylinder} onChange={v => { setFilterCylinder(v); setPage(1); }} options={CYL_OPTS} style={{ width:170 }} />
      </FilterBar>

      <ActiveFilters
        filters={activeFilters}
        onRemove={f => { if(f.key==='status') setFilterStatus('All'); else setFilterCylinder('All'); }}
      />

      <Card noPad className="au d2">
        <DataTable
          loading={loading}
          empty={rows.length === 0 ? 'No customers found. Add your first customer to get started.' : null}
          headers={['Customer','Connection','Phone','Cylinder','Last Booking','Status','Actions']}
        >
          {paged.map(c => {
            const lb = c.bookings?.[0];
            const days = lb ? Math.floor((Date.now() - new Date(lb.bookingDate)) / 86400000) : null;
            return (
              <Tr key={c.id}>
                <Td>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <Avatar name={c.name} size={32} />
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{c.name}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{c.email || '—'}</div>
                    </div>
                  </div>
                </Td>
                <Td><span style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--text-secondary)' }}>{c.connectionNo}</span></Td>
                <Td style={{ color:'var(--text-secondary)', fontSize:13 }}>{c.phone}</Td>
                <Td><Badge type="gray">{c.cylinderType?.split(' ').slice(0,2).join(' ')}</Badge></Td>
                <Td>
                  {lb ? (
                    <div>
                      <div style={{ fontSize:12, color:'var(--text-secondary)' }}>{lb.bookingDate}</div>
                      <div style={{ fontSize:11, color: days >= 30 ? 'var(--accent-amber)' : 'var(--accent-green)', fontWeight:600 }}>{days}d ago</div>
                    </div>
                  ) : <span style={{ fontSize:12, color:'var(--text-muted)' }}>Never booked</span>}
                </Td>
                <Td><Badge type={c.status === 'Active' ? 'success' : 'danger'} dot>{c.status}</Badge></Td>
                <Td>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                    <Btn size="xs" variant="ghost" onClick={() => openHist(c)}>History</Btn>
                    <Btn size="xs" variant="secondary" onClick={() => openEdit(c)}>Edit</Btn>
                    {canAdmin && <Btn size="xs" variant="danger" onClick={() => remove(c.id)}>✕</Btn>}
                  </div>
                </Td>
              </Tr>
            );
          })}
        </DataTable>
        <Paginator
          page={page} totalPages={totalPages}
          totalItems={rows.length} perPage={perPage}
          onPage={setPage} onPerPage={p => { setPerPage(p); setPage(1); }}
        />
      </Card>

      {/* Add / Edit modal */}
      {modal === 'form' && (
        <Modal
          title={editId ? 'Edit Customer' : 'Add New Customer'}
          subtitle={editId ? 'Update customer details below' : 'Fill in the details to register a new customer'}
          onClose={() => setModal(null)}
        >
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14 }}>
              <FormGroup label="Full Name" required>
                <input value={form.name} onChange={e => F('name',e.target.value)} placeholder="Full legal name" className="form-input" />
              </FormGroup>
              <FormGroup label="Phone" required>
                <input value={form.phone} onChange={e => F('phone',e.target.value)} placeholder="10-digit mobile" className="form-input" />
              </FormGroup>
              <FormGroup label="Connection No." required>
                <input value={form.connectionNo} onChange={e => F('connectionNo',e.target.value)} placeholder="KL-01-XXXXX" className="form-input" />
              </FormGroup>
              <FormGroup label="Aadhar Number">
                <input value={form.aadhar} onChange={e => F('aadhar',e.target.value)} placeholder="XXXX-XXXX-XXXX" className="form-input" />
              </FormGroup>
              <FormGroup label="Email Address">
                <input type="email" value={form.email} onChange={e => F('email',e.target.value)} placeholder="email@example.com" className="form-input" />
              </FormGroup>
              <FormGroup label="Cylinder Type">
                <select value={form.cylinderType} onChange={e => F('cylinderType',e.target.value)} className="form-select">
                  {CYLINDERS.map(t => <option key={t}>{t}</option>)}
                </select>
              </FormGroup>
              <FormGroup label="Status">
                <select value={form.status} onChange={e => F('status',e.target.value)} className="form-select">
                  <option>Active</option><option>Inactive</option>
                </select>
              </FormGroup>
            </div>
            <FormGroup label="Address">
              <textarea value={form.address} onChange={e => F('address',e.target.value)} placeholder="Full residential or commercial address" className="form-textarea" />
            </FormGroup>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:6 }}>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : editId ? 'Update Customer' : 'Add Customer'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* History modal */}
      {modal === 'history' && hist && (
        <Modal title="Booking History" subtitle={`${hist.name} · ${hist.connectionNo}`} onClose={() => setModal(null)} maxWidth={620}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
            {[
              ['Bookings', histBk.length, 'var(--brand)'],
              ['Delivered', histBk.filter(b => b.status==='Delivered').length, 'var(--accent-green)'],
              ['Total Spent', '₹' + histBk.filter(b => b.status==='Delivered').reduce((s,b) => s+parseFloat(b.amount||0), 0).toLocaleString('en-IN'), 'var(--accent-blue)'],
            ].map(([l,v,c]) => (
              <div key={l} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-md)', padding:'14px 10px', textAlign:'center' }}>
                <div style={{ fontSize:20, fontWeight:800, color:c, letterSpacing:'-0.02em' }}>{v}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4, fontWeight:500 }}>{l}</div>
              </div>
            ))}
          </div>

          {histBk.length === 0 ? (
            <div style={{ textAlign:'center', padding:'32px 0', color:'var(--text-muted)', fontSize:13 }}>No bookings found for this customer</div>
          ) : histBk.map((b, i) => {
            const sc = STATUS_CONF[b.status] || { type:'gray' };
            const dotColor = { success:'var(--accent-green)', warning:'var(--accent-amber)', info:'var(--accent-blue)', danger:'var(--accent-red)', gray:'var(--text-muted)' }[sc.type];
            return (
              <div key={b.id} style={{
                display:'flex', alignItems:'center', gap:12, padding:'12px 0',
                borderBottom: i < histBk.length-1 ? '1px solid var(--border-subtle)' : 'none',
              }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:dotColor, flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)' }}>
                    <span style={{ fontFamily:'var(--font-mono)', color:'var(--brand)', fontSize:12 }}>{b.bookingNo}</span>
                    <span style={{ color:'var(--text-muted)', margin:'0 6px' }}>·</span>
                    {b.cylinderType}
                  </div>
                  <div style={{ fontSize:11.5, color:'var(--text-muted)', marginTop:2 }}>
                    {b.bookingDate}{b.deliveryDate ? ` → ${b.deliveryDate}` : ''} · ₹{b.amount} · {b.creator?.name || '—'}
                  </div>
                </div>
                <Badge type={sc.type}>{b.status}</Badge>
              </div>
            );
          })}

          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:20 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Close</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
